import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store/redux/store/store';
import { MessageSquare } from 'lucide-react';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { PageLayout } from '../../../components/ui/PageLayout';
import { teamHubApi } from '../../../api/PreformanceManagement/TeamHub';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import {
  TeamHubPostView,
  TeamHubPostInput,
  TeamHubPostCommentInput,
  TeamHubFilterOptions,
} from '../../../models/preformanceManagement';
import UpdateComposer from './components/UpdateComposer';
import UpdateFilters from './components/UpdateFilters';
import UpdatesFeed from './components/UpdatesFeed';

const TeamHub: React.FC = () => {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const userId = useSelector((state: RootState) => (state as any).user?.id || (state as any).user?.userId || '');
  const userName = useSelector((state: RootState) => (state as any).user?.fullName || (state as any).user?.firstName || (state as any).user?.userName || '');
  const userDepartment = useSelector((state: RootState) => (state as any).user?.department?.name || (state as any).user?.departmentName || '');
  const userRole = useSelector((state: RootState) => (state as any).user?.role);

  const [filters, setFilters] = useState({
    dateRange: '',
    updateType: '',
    department: '',
    searchQuery: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [posts, setPosts] = useState<TeamHubPostView[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  const loadPosts = useCallback(async () => {
    try {
      setLoadingPosts(true);

      const apiFilters: TeamHubFilterOptions = {
        search: filters.searchQuery,
        postType: filters.updateType || undefined,
        department: filters.department || undefined,
      };

      const result = await teamHubApi.getPosts(apiFilters);

      if (result.payload) {
        setPosts(result.payload);
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingPosts(false);
    }
  }, [filters.searchQuery, filters.updateType, filters.department]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleFilterChange = useCallback((newFilters: any) => {
    setFilters(prev => ({
      ...prev,
      dateRange: newFilters.dateRange || '',
      updateType: newFilters.updateType || '',
      searchQuery: newFilters.searchQuery || ''
    }));
  }, []);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setFilters(prev => ({ ...prev, searchQuery: query }));
  }, []);

  const handlePublishUpdate = useCallback(async (update: any) => {
    try {
      const postInput: TeamHubPostInput = {
        content: update.content || '',
        postType: update.postType || 'update',
        department: update.department || userDepartment || '',
        tags: update.tags || [],
        attachmentUrls: update.attachmentUrls || [],
      };

      const result = await teamHubApi.createPost(postInput);

      if (result.payload) {
        await loadPosts();
        showToast(t('performance.teamHub.postCreated'), 'success');
      } else {
        showToast(getApiErrorMessage(result, t, t('performance.teamHub.failedCreatePost')), 'error');
      }
    } catch {
      showToast(t('performance.teamHub.failedCreatePost'), 'error');
    }
  }, [userDepartment, loadPosts, showToast, t]);

  const handleDeletePost = useCallback(async (postId: number) => {
    if (window.confirm(t('performance.teamHub.confirmDeletePost'))) {
      try {
        const result = await teamHubApi.deletePost(postId);

        if (result.status === 200 || result.payload) {
          setPosts(prev => prev.filter(post => post.id !== postId));
          showToast(t('performance.teamHub.postDeleted'), 'success');
        } else {
          showToast(getApiErrorMessage(result, t, t('performance.teamHub.failedDeletePost')), 'error');
        }
      } catch {
        showToast(t('performance.teamHub.failedDeletePost'), 'error');
      }
    }
  }, [showToast, t]);

  const handleAddComment = useCallback(async (postId: number, comment: string) => {
    try {
      const commentInput: TeamHubPostCommentInput = {
        postId,
        content: comment,
      };

      const result = await teamHubApi.createComment(commentInput);

      if (result.payload) {
        await loadPosts();
        showToast(t('performance.teamHub.commentAdded'), 'success');
      } else {
        showToast(getApiErrorMessage(result, t, t('performance.teamHub.failedAddComment')), 'error');
      }
    } catch {
      showToast(t('performance.teamHub.failedAddComment'), 'error');
    }
  }, [loadPosts, showToast, t]);

  const handleDeleteComment = useCallback(async (commentId: number, postId: number) => {
    if (window.confirm(t('performance.teamHub.confirmDeleteComment'))) {
      try {
        const result = await teamHubApi.deleteComment(commentId);

        if (result.status === 200 || result.payload) {
          setPosts(prev =>
            prev.map(post => {
              if (post.id === postId) {
                return {
                  ...post,
                  comments: (post.comments || []).filter(c => c.id !== commentId),
                  commentCount: Math.max(0, (post.commentCount || 0) - 1)
                };
              }
              return post;
            })
          );
          showToast(t('performance.teamHub.commentDeleted'), 'success');
        } else {
          showToast(getApiErrorMessage(result, t, t('performance.teamHub.failedDeleteComment')), 'error');
        }
      } catch {
        showToast(t('performance.teamHub.failedDeleteComment'), 'error');
      }
    }
  }, [showToast, t]);

  return (
    <>
      <PageLayout
        title={t('performance.teamHub.title')}
        description={t('performance.teamHub.subtitle')}
        icon={MessageSquare}
        accentColor="rose"
        maxWidth="1600px"
      >
        {/* Main Content */}
        <div className="py-2 flex justify-center">
            <div className="w-full md:w-4/5 max-w-[1400px] mx-auto flex flex-col gap-4">
              <UpdateComposer
                onPublish={handlePublishUpdate}
                currentUserDepartment={userDepartment}
              />

              <UpdateFilters
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
              />

              <UpdatesFeed
                posts={posts}
                loading={loadingPosts}
                filters={filters}
                searchQuery={searchQuery}
                currentUserId={userId}
                onDeletePost={handleDeletePost}
                onAddComment={handleAddComment}
                onDeleteComment={handleDeleteComment}
                onRefresh={loadPosts}
              />
            </div>
        </div>
      </PageLayout>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </>
  );
};

export default TeamHub;
